// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Card, Checkbox, Flex, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { createReference, getReferenceString } from '@medplum/core';
import type { Encounter, Patient, Practitioner, Provenance, Reference, Task } from '@medplum/fhirtypes';
import { Loading, useMedplum, useResource } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { ConcludeSessionModal } from '../../components/appointment/ConcludeSessionModal';
import { EncounterChartForm } from '../../components/encounter/EncounterChartForm';
import { EncounterHeader } from '../../components/encounter/EncounterHeader';
import { useEncounterChart } from '../../hooks/useEncounterChart';
import { ChartNoteStatus } from '../../types/encounter';
import { concludeEncounterForLockedNote } from '../../utils/encounter';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';

const FHIR_ACT_REASON_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ActReason';
const FHIR_PROVENANCE_PARTICIPANT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/provenance-participant-type';
const FHIR_DOCUMENT_COMPLETION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion';

const TASK_COMPLETED_STATUSES = new Set<Task['status']>([
  'completed',
  'cancelled',
  'failed',
  'rejected',
  'entered-in-error',
]);

export function AppointmentSessionPage(): JSX.Element | null {
  const { appointmentId } = useParams();
  const medplum = useMedplum();

  const [encounterRef, setEncounterRef] = useState<Reference<Encounter>>();
  const [initialLoading, setInitialLoading] = useState(true);
  const [provenances, setProvenances] = useState<Provenance[]>([]);
  const [chartNoteStatus, setChartNoteStatus] = useState<ChartNoteStatus>(ChartNoteStatus.Unsigned);
  const [concludeOpened, { open: openConclude, close: closeConclude }] = useDisclosure(false);
  const [checklistHidden, { toggle: toggleChecklist }] = useDisclosure(false);
  const chartSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    if (!appointmentId) {
      setInitialLoading(false);
      return;
    }
    medplum
      .searchResources('Encounter', { appointment: `Appointment/${appointmentId}`, _count: '1' })
      .then((encounters) => {
        if (encounters[0]?.id) {
          setEncounterRef({ reference: `Encounter/${encounters[0].id}` });
        }
      })
      .catch(showErrorNotification)
      .finally(() => setInitialLoading(false));
  }, [medplum, appointmentId]);

  const {
    encounter,
    claim,
    practitioner,
    tasks,
    checklistItems,
    clinicalImpression,
    clinicalImpressionLoaded,
    chargeItems,
    appointment,
    setEncounter,
    setClaim,
    setPractitioner,
    setTasks,
    setChargeItems,
  } = useEncounterChart(encounterRef);

  const patientRef = encounter?.subject as Reference<Patient> | undefined;
  const patient = useResource(patientRef);

  useEffect(() => {
    if (!encounter) {
      return;
    }
    const fetchProvenance = async (): Promise<void> => {
      const provenance = await medplum.searchResources('Provenance', `target=${getReferenceString(encounter)}`);
      setProvenances(provenance);
      if (provenance.length > 0 && clinicalImpression?.status === 'completed') {
        setChartNoteStatus(ChartNoteStatus.SignedAndLocked);
      } else if (provenance.length > 0) {
        setChartNoteStatus(ChartNoteStatus.Signed);
      } else {
        setChartNoteStatus(ChartNoteStatus.Unsigned);
      }
    };
    fetchProvenance().catch(showErrorNotification);
  }, [clinicalImpression, encounter, medplum]);

  const handleSign = async (practitionerRef: Reference<Practitioner>, lock: boolean): Promise<void> => {
    if (!encounter) {
      return;
    }
    if (lock) {
      const tasksToUpdate = tasks.filter((task) => !TASK_COMPLETED_STATUSES.has(task.status));
      const updatedTasks = await Promise.all(
        tasksToUpdate.map((task) => medplum.updateResource<Task>({ ...task, status: 'completed' }))
      );
      setTasks(tasks.map((task) => updatedTasks.find((t) => t.id === task.id) ?? task));
    }
    const newProvenance = await medplum.createResource<Provenance>(
      withTenantScope({
        resourceType: 'Provenance',
        target: [createReference(encounter)],
        recorded: new Date().toISOString(),
        reason: [{ coding: [{ system: FHIR_ACT_REASON_SYSTEM, code: 'SIGN', display: 'Signed' }] }],
        agent: [
          {
            type: { coding: [{ system: FHIR_PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'author' }] },
            who: practitionerRef,
          },
        ],
        signature: [
          {
            type: [{ system: FHIR_DOCUMENT_COMPLETION_SYSTEM, code: 'LA', display: 'legally authenticated' }],
            when: new Date().toISOString(),
            who: practitionerRef,
          },
        ],
      })
    );
    setProvenances([...provenances, newProvenance]);
    setChartNoteStatus(lock ? ChartNoteStatus.SignedAndLocked : ChartNoteStatus.Signed);

    // Locking legally authenticates the record, so conclude the visit as
    // successful — see concludeEncounterForLockedNote.
    if (lock) {
      const concluded = await concludeEncounterForLockedNote(medplum, encounter, appointment);
      if (concluded) {
        setEncounter(concluded);
      }
    }
  };

  const handleTaskToggle = async (task: Task): Promise<void> => {
    const newStatus: Task['status'] = TASK_COMPLETED_STATUSES.has(task.status) ? 'in-progress' : 'completed';
    try {
      const updated = await medplum.updateResource({ ...task, status: newStatus });
      setTasks(tasks.map((t) => (t.id === task.id ? updated : t)));
    } catch (err) {
      showErrorNotification(err);
    }
  };

  if (initialLoading || !patient || !encounter || !clinicalImpressionLoaded) {
    return <Loading />;
  }

  const isLocked = chartNoteStatus === ChartNoteStatus.SignedAndLocked;

  return (
    <Stack gap={0}>
      <Flex align="flex-start" gap={0}>
        {/* Encounter Checklist sidebar */}
        <Box p={16} style={{ width: 311, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
          <Card withBorder p={20} style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.01)' }}>
            <Group justify="space-between" mb={10} align="center">
              <Text fw={700} size="lg">
                Encounter Checklist
              </Text>
              <Anchor component="button" size="xs" fw={600} onClick={toggleChecklist}>
                {checklistHidden ? 'Show' : 'Hide'}
              </Anchor>
            </Group>
            {!checklistHidden && (
              <Box p={20} bg="gray.0" style={{ borderRadius: 8 }}>
                <Stack gap={10}>
                  {checklistItems.map((item) => (
                    <Checkbox
                      key={item.id}
                      label={item.label}
                      checked={item.task ? TASK_COMPLETED_STATUSES.has(item.task.status) : false}
                      onChange={() => item.task && handleTaskToggle(item.task).catch(showErrorNotification)}
                      disabled={isLocked || !item.task}
                    />
                  ))}
                  {checklistItems.length === 0 && (
                    <Text size="sm" c="dimmed">
                      No checklist items
                    </Text>
                  )}
                </Stack>
              </Box>
            )}
          </Card>
        </Box>

        {/* Encounter Header + Form */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <EncounterHeader encounter={encounter} appointment={appointment} onConclude={openConclude} />
          <EncounterChartForm
            encounter={encounter}
            appointment={appointment}
            patient={patient}
            chargeItems={chargeItems}
            claim={claim}
            practitioner={practitioner}
            provenances={provenances}
            chartNoteStatus={chartNoteStatus}
            isLocked={isLocked}
            saveChartRef={chartSaveRef}
            onSign={handleSign}
            setEncounter={setEncounter}
            setPractitioner={setPractitioner}
            setChargeItems={setChargeItems}
            setClaim={setClaim}
          />
        </Box>
      </Flex>

      <ConcludeSessionModal
        opened={concludeOpened}
        onClose={closeConclude}
        encounter={encounter}
        appointment={appointment}
        onBeforeConclude={() => chartSaveRef.current()}
        onConcluded={(concluded) => {
          // Stay on the note: concluding the session does not sign the note, so the
          // provider still needs this page to sign and lock it.
          setEncounter(concluded);
          closeConclude();
        }}
      />
    </Stack>
  );
}
